import {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useFilterLandingBinStore } from "stores/filterLandingBinStore";

import { DEFAULT_MAX_BINS } from "constants/mapSeries.constants";
import { isForecastTrumpingSource } from "constants/settings.constants";
import {
  setGlobalGroupBy,
  setPlaceholderBinsEnabled
} from "store/features/groupBy/groupBySlice";
import { buildForecastTrumpingSettings } from "utils/data-table";
import { toPdenEnum } from "utils/toPdenEnum";

import { retireFreshSessionMaxBinDefault } from "hooks/useFreshSessionMaxBinDefault";
import { useMaxBinDefaults } from "hooks/useMaxBinDefaults";

import {
  BIN_TYPES,
  BinType,
  IGroupBy,
  LegendItemModel,
  QuantileState,
  preserveGroupMode
} from "models";
import { mBinSize } from "models/binSize";

import { useUserDefaults } from "components/user/hooks";

import { MONTH_TIMESTAMP } from "../DateBinSettings";
import { useBinSizeQuery } from "../queries";
import { GroupByFilterDependencies, UseBinSettingsControllerResult } from "../types";
import { updateGroupBy } from "../util";
import {
  checkedValueCount,
  checkedValueCountInQuery,
  exceedsMaxBins
} from "../util/checkedBins";
import { getStoredBinsFromSession } from "../util/getStoredBinsFromSession";
import { setStoredBinsToSession } from "../util/setStoredBinsToSession";
import { UseSessionBinStorageResult } from "./useSessionBinStorage";

export interface UseBinSettingsControllerProps {
  deps: GroupByFilterDependencies;
  session: UseSessionBinStorageResult;
  showGroupBy: boolean;
  setShowGroupBy: Dispatch<SetStateAction<boolean>>;
  checkedLegendItemsRef: MutableRefObject<LegendItemModel[]>;
  // Checked-Item Filtering's stable handler for "bins changed" — replaces the
  // former performBatchOption("uncheckAll") + manual ref-clear + subject.next
  // trio; that whole uncheck-and-reapply sequence is now internal to it.
  onBinsChanged: () => void;
  // Bumped by Checked-Item Filtering when checking items should auto-lock the
  // bins. Bin Settings owns the actual lock toggle, so it reacts to this value
  // rather than being called back into (avoids a ref-trampoline in either
  // direction — see CONTEXT.md).
  autoLockRequestId: number;
  // The checked values the lock must fit, read when autoLockRequestId changes.
  autoLockCheckedCount?: number;
}

// EVA-6412 SR-004b (NEW, T024) — owns bin state, bin compute, updateBin,
// lock/unlock, quantile-transition uncheck, normalize-by recompute, and the
// bin-size fetch trigger (updateField's bin portion), lifted verbatim out of
// GroupByFilterActivity.tsx. Consumes useSessionBinStorage + useBinSizeQuery
// + deps — never entityKind directly (fixes P2). The fetch call moment is
// reproduced via a small pending-fetch ref that gates useBinSizeQuery's
// `enabled` and an effect that applies the query response exactly as
// the old `.then()` callback did (fixes P3 — same URL/params/response
// handling, BP-003; only the calling mechanism changes).
export const useBinSettingsController = ({
  deps,
  session,
  showGroupBy,
  setShowGroupBy,
  checkedLegendItemsRef,
  onBinsChanged,
  autoLockRequestId,
  autoLockCheckedCount = 0
}: UseBinSettingsControllerProps): UseBinSettingsControllerResult => {
  const dispatch = useDispatch();
  const defaults = useUserDefaults();
  const { localBinsRef, localPerformanceDateRangeRef, updateSessionStorage } = session;
  const { groupBy, filterId, placeholderBinsEnabled, currentFilter } = deps;
  const maxBinDefaultsEnabled = useMaxBinDefaults() === true;

  function getInitialBinState(): mBinSize {
    if (
      groupBy.dataType.toLowerCase() !== "number" ||
      groupBy.dataType.toLowerCase() !== "integer" ||
      groupBy.dataType.toLowerCase() !== "date"
    ) {
      return { ...groupBy.bin };
    }
    let initialState = {
      ...groupBy.bin,
      BinType: defaults?.binSettings?.type ?? ("BinSize" as BinType)
    };
    if (defaults?.binSettings?.quantile) {
      initialState = { ...initialState, Quantile: defaults.binSettings.quantile };
    }
    return initialState;
  }

  const [bin, setBin] = useState<mBinSize>(getInitialBinState());
  const [minCountWarningVisible, setMinCountWarningVisible] = useState(false);
  const filterIdRef = useRef<string>(filterId.id);

  const canEditBin = groupBy?.dataType.toLowerCase() !== "text";

  const saveAndUpdateBinSettings = useCallback(
    (property: string, binSettings: mBinSize, groupByObject: IGroupBy) => {
      updateSessionStorage(property, binSettings);
      dispatch(setGlobalGroupBy(groupByObject));
      setBin(binSettings);
    },
    [dispatch, updateSessionStorage]
  );

  // Pending-fetch state: gates useBinSizeQuery's `enabled` to the exact
  // moments updateField used to call axios.get (DQ-1). pendingFieldGbRef holds
  // the in-flight fieldGb so the response-applying effect can mutate it
  // exactly as the original .then() body did.
  const pendingFieldGbRef = useRef<{ fieldGb: IGroupBy; updateBinsOnly: boolean } | null>(
    null
  );
  const [fetchParams, setFetchParams] = useState<
    Maybe<{
      filterId: string;
      property: string;
      normalizeField: Maybe<string>;
      per: Maybe<number | string>;
      pdenSource: number;
      useDynamicBins: boolean;
      forecastTrumpingSettings?: ReturnType<typeof buildForecastTrumpingSettings>;
      performanceDateRange?: IGroupBy["performanceDateRange"];
    }>
  >(null);

  const binSizeQuery = useBinSizeQuery({
    filterId: fetchParams?.filterId ?? "",
    property: fetchParams?.property ?? "",
    normalizeField: fetchParams?.normalizeField,
    per: fetchParams?.per,
    pdenSource: fetchParams?.pdenSource ?? 0,
    entityKind: deps.groupBy?.entityKind,
    useDynamicBins: fetchParams?.useDynamicBins ?? false,
    forecastTrumpingSettings: fetchParams?.forecastTrumpingSettings,
    performanceDateRange: fetchParams?.performanceDateRange,
    enabled: !!fetchParams
  });

  const toggleGroupByList = useCallback(
    (updateBinsOnly: boolean) => {
      if (!updateBinsOnly) {
        setShowGroupBy(!showGroupBy);
      }
    },
    [setShowGroupBy, showGroupBy]
  );

  // MaxBins before lockBinsToFit first raised it. The raise lives only in the
  // group-by and the input; storage keeps this value, so a reload starts from it.
  const preRaiseRef =
    useRef<Nullable<{ property: string; maxBins: mBinSize["MaxBins"] }>>(null);
  // Mirrors preRaiseRef so a raise locks Bin Settings on the next render.
  const [raisedProperty, setRaisedProperty] = useState<Nullable<string>>(null);
  const setPreRaise = useCallback(
    (raise: Nullable<{ property: string; maxBins: mBinSize["MaxBins"] }>) => {
      preRaiseRef.current = raise;
      setRaisedProperty(raise?.property ?? null);
    },
    []
  );

  // A raise waiting for its filter's new id; see the effect below lockBinsToFit.
  const pendingRaiseRef = useRef<Nullable<{ property: string; maxBins: number }>>(null);

  // The form a bin is stored in: the raise never is, only the MaxBins before it.
  const toStored = useCallback((property: string, b: mBinSize): mBinSize => {
    const raise = preRaiseRef.current;
    return raise?.property === property ? { ...b, MaxBins: raise.maxBins } : b;
  }, []);

  const updateField = useCallback(
    (fieldGb: IGroupBy, updateBinsOnly = false) => {
      fieldGb.mode = preserveGroupMode(groupBy.mode, fieldGb.dataType);
      fieldGb.entityKind = deps.groupBy.entityKind;

      const freshFromSession = getStoredBinsFromSession();

      if (!fieldGb.performanceDateRange) {
        const storedDateRange = localPerformanceDateRangeRef.current[fieldGb.property];
        if (storedDateRange) {
          fieldGb.performanceDateRange = storedDateRange;
        }
      }

      // The bin-size endpoint answers 0 when the pinned production source holds
      // no data for these wells. Zero is never a usable bin width, so a stored 0
      // has to count as "never computed" — otherwise the early return below
      // hands it back forever and the field stays unbinned even once a source
      // with data is picked. Only 0: an empty bin size is a deliberate reset
      // elsewhere, and a Text field legitimately stores one. A bin size reaches
      // here as a string as often as a number, so compare on the number.
      const isNumericField =
        fieldGb.dataType?.toLowerCase() === "integer" ||
        fieldGb.dataType?.toLowerCase() === "number";
      const isZeroBin = (value: Maybe<number | string>) =>
        isNumericField && value !== "" && value != null && Number(value) === 0;

      const storedBin = freshFromSession[fieldGb.property];
      if (storedBin && !updateBinsOnly && !isZeroBin(storedBin.BinSize)) {
        localBinsRef.current = freshFromSession;
        fieldGb.bin = { ...storedBin };
        // The stored MaxBins is the one before any raise, so values still checked on
        // a focus the user returns to are fitted again.
        const checkedCount = checkedValueCount(deps.propertiesFilter, fieldGb.property);
        if (exceedsMaxBins(storedBin.MaxBins, checkedCount)) {
          setPreRaise({
            property: fieldGb.property,
            maxBins: storedBin.MaxBins
          });
          fieldGb.bin.MaxBins = checkedCount;
        }
        dispatch(setGlobalGroupBy(fieldGb));
        setBin(fieldGb.bin);
        toggleGroupByList(updateBinsOnly);
        return;
      }

      const binSize = fieldGb.bin?.BinSize;
      if (
        (binSize === "" || isZeroBin(binSize)) &&
        filterIdRef.current &&
        isNumericField
      ) {
        const normalizeField = deps.useNormalizeBy
          ? encodeURIComponent(deps.normalizeBy?.field)
          : "''";
        const per = deps.useNormalizeBy ? deps.normalizeBy?.per : 0;
        const useDynamicBins = fieldGb.bin?.UseDynamicBins ?? false;
        const pdenSource = toPdenEnum(fieldGb.pdenSource);

        const isForecastTrumping = isForecastTrumpingSource(fieldGb.forecastFolder);
        if (isForecastTrumping) {
          fieldGb.forecastTrumpingSettings = buildForecastTrumpingSettings(
            deps.checkedGlobalForecasts,
            deps.specialForecastFolders,
            deps.syncWells?.isSync ? deps.syncWells.schema : undefined,
            deps.syncWells?.isSync ? deps.syncWells.forecastRescat : undefined,
            deps.forecastReserveCategory
          );
        }

        pendingFieldGbRef.current = { fieldGb, updateBinsOnly };
        setFetchParams({
          filterId: filterIdRef.current,
          property: fieldGb.property,
          normalizeField,
          per,
          pdenSource,
          useDynamicBins,
          forecastTrumpingSettings: fieldGb.forecastTrumpingSettings,
          performanceDateRange: fieldGb.performanceDateRange
        });
      } else {
        if (fieldGb.dataType === "Date" && !fieldGb.bin.BinSize) {
          const newFieldGb = {
            ...fieldGb,
            bin: { ...fieldGb.bin, BinSize: MONTH_TIMESTAMP }
          };
          saveAndUpdateBinSettings(newFieldGb.property, newFieldGb.bin, newFieldGb);
        } else {
          saveAndUpdateBinSettings(fieldGb.property, fieldGb.bin, fieldGb);
        }
        toggleGroupByList(updateBinsOnly);
      }
    },
    [
      groupBy.mode,
      deps.groupBy.entityKind,
      deps.useNormalizeBy,
      deps.normalizeBy,
      deps.propertiesFilter,
      deps.checkedGlobalForecasts,
      deps.specialForecastFolders,
      deps.forecastReserveCategory,
      deps.syncWells,
      dispatch,
      localBinsRef,
      localPerformanceDateRangeRef,
      saveAndUpdateBinSettings,
      toggleGroupByList
    ]
  );

  // Applies the bin-size response exactly as the original axios .then() body did.
  useEffect(() => {
    if (!pendingFieldGbRef.current || binSizeQuery.isFetching) return;

    const { fieldGb, updateBinsOnly } = pendingFieldGbRef.current;

    if (binSizeQuery.error) {
      toast.error(`Failed to get bin size for ${fieldGb.title}`);
      pendingFieldGbRef.current = null;
      setFetchParams(null);
      return;
    }

    if (!binSizeQuery.data) return;

    const binResponse = binSizeQuery.data;
    const binSizeValue = binResponse.binSize;
    fieldGb.bin.BinSize = (binSizeValue ?? "") as number | "";
    fieldGb.bin.LessThan =
      binResponse.lessThan === "" ? null : (binResponse.lessThan as never);
    fieldGb.bin.GreaterThan =
      binResponse.greaterThan === "" ? null : (binResponse.greaterThan as never);
    fieldGb.bin.BinType = updateBinsOnly
      ? fieldGb.bin.BinType
      : defaults?.binSettings?.type;
    if (!fieldGb.bin.Quantile?.quantileType || !fieldGb.bin.Quantile?.numQuantiles) {
      fieldGb.bin.Quantile = defaults?.binSettings?.quantile;
    }
    fieldGb.bin.IsLocked = binResponse.isLocked ?? false;

    saveAndUpdateBinSettings(fieldGb.property, fieldGb.bin, fieldGb);
    toggleGroupByList(updateBinsOnly);

    pendingFieldGbRef.current = null;
    setFetchParams(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [binSizeQuery.data, binSizeQuery.isFetching, binSizeQuery.error]);

  useEffect(() => {
    filterIdRef.current = filterId.id;

    const currentGroupBy: IGroupBy = {
      ...groupBy,
      bin: {
        BinSize: "",
        GreaterThan: "",
        LessThan: "",
        MinSize: "",
        MaxBins: groupBy.bin?.MaxBins ?? "",
        MaxBinsSortOrder: groupBy.bin?.MaxBinsSortOrder,
        MaxBinsSortField: groupBy.bin?.MaxBinsSortField,
        BinType: "BinSize",
        UseDynamicBins: groupBy.bin?.UseDynamicBins ?? false
      }
    };

    if (
      groupBy.bin?.BinType === "BinSize" &&
      !groupBy.bin?.IsLocked &&
      groupBy.bin?.UseDynamicBins
    ) {
      updateField(currentGroupBy, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filterId, groupBy.bin?.IsLocked]);

  // recalculate the bins when normalization is turned on or off
  useEffect(() => {
    if (
      groupBy.bin?.BinType === "BinSize" &&
      !groupBy.bin?.IsLocked &&
      groupBy.bin?.UseDynamicBins
    ) {
      const currentGroupBy: IGroupBy = {
        ...groupBy,
        bin: {
          BinSize: "",
          GreaterThan: "",
          LessThan: "",
          MinSize: "",
          MaxBins: groupBy.bin?.MaxBins ?? DEFAULT_MAX_BINS,
          MaxBinsSortOrder: groupBy.bin?.MaxBinsSortOrder ?? "WellCount",
          MaxBinsSortField: groupBy.bin?.MaxBinsSortField,
          BinType: "BinSize",
          UseDynamicBins: groupBy.bin?.UseDynamicBins ?? false
        }
      };
      updateField(currentGroupBy, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deps.useNormalizeBy, deps.normalizeBy]);

  useEffect(() => {
    setBin(groupBy.bin);
  }, [groupBy]);

  useEffect(() => {
    setMinCountWarningVisible(false);
    const activeFilter = currentFilter?.query?.children_nodes;
    if (activeFilter) {
      activeFilter.forEach((node, i) => {
        if (
          node.predicates.some(
            // A source key is only there on predicates that came from a legend
            // bin; polygon, well-list and older saved predicates carry none.
            (x) => x.sourceKey?.includes("Less Than") && activeFilter.length - 1 !== i
          )
        ) {
          if (node.predicates[0].property === groupBy.property) {
            setMinCountWarningVisible(true);
          }
        }
      });
    }
  }, [currentFilter, groupBy]);

  const storeBin = useCallback(
    (newBin: mBinSize) => {
      const property = groupBy.property;
      const stored = toStored(property, newBin);
      const newLocals: Record<string, mBinSize> = {};
      Object.keys(localBinsRef.current).forEach((key) => {
        newLocals[key] = { ...localBinsRef.current[key] };
      });
      newLocals[property] = { ...stored };
      const localBin = { ...localBinsRef.current, ...newLocals };
      localBinsRef.current = localBin;
      setStoredBinsToSession(localBin);
      setBin(newBin);
    },
    [groupBy.property, localBinsRef, toStored]
  );

  const updateBin = useCallback(
    (val: Partial<mBinSize>) => {
      // A MaxBins the user changes replaces the raise; a full-bin commit that
      // carries the current one keeps it.
      if ("MaxBins" in val && val.MaxBins !== bin.MaxBins) {
        setPreRaise(null);
        pendingRaiseRef.current = null;
      }
      storeBin(Object.assign({}, bin, val));
    },
    [bin, storeBin, setPreRaise]
  );

  const updateGroupByBin = useCallback(
    (binType: BinType, quantile: QuantileState, useDynamicBins: boolean) => {
      // The user's own bins replace any still waiting for the well filter.
      if (deps.isWell) useFilterLandingBinStore.getState().setPending(null);
      let newBinType = binType;
      if (binType === BIN_TYPES.Dynamic) {
        newBinType = BIN_TYPES.BinSize;
      }
      const currentBinType = bin.BinType;
      const isTransitionToQuantile =
        newBinType === BIN_TYPES.Quantile && currentBinType !== BIN_TYPES.Quantile;
      const isTransitionFromQuantile =
        newBinType !== BIN_TYPES.Quantile && currentBinType === BIN_TYPES.Quantile;
      const isSwitchingToDynamic = binType === BIN_TYPES.Dynamic && !bin.UseDynamicBins;

      if (isTransitionToQuantile || isTransitionFromQuantile) {
        onBinsChanged();
      }

      const newBin = {
        BinSize: bin.BinSize,
        MinSize: bin.MinSize,
        MaxBins: bin.MaxBins,
        MaxBinsSortOrder: bin.MaxBinsSortOrder,
        MaxBinsSortField: bin.MaxBinsSortField,
        GreaterThan: bin.GreaterThan,
        LessThan: bin.LessThan,
        Quantile: quantile,
        BinType: newBinType,
        UseDynamicBins: useDynamicBins,
        IsLocked:
          isSwitchingToDynamic && checkedLegendItemsRef.current.length === 0
            ? false
            : bin.IsLocked
      };

      const newGroupBy = Object.assign({}, groupBy, { bin: newBin });

      updateBin(newBin);
      updateSessionStorage(groupBy.property, toStored(groupBy.property, newBin));
      dispatch(setGlobalGroupBy(newGroupBy));

      if (isSwitchingToDynamic && checkedLegendItemsRef.current.length === 0) {
        const currentGroupBy: IGroupBy = {
          ...newGroupBy,
          bin: {
            BinSize: "",
            GreaterThan: "",
            LessThan: "",
            MinSize: "",
            MaxBins: newBin.MaxBins ?? "",
            MaxBinsSortOrder: newBin.MaxBinsSortOrder ?? "WellCount",
            MaxBinsSortField: newBin.MaxBinsSortField,
            BinType: "BinSize",
            UseDynamicBins: true,
            IsLocked: false
          }
        };
        updateField(currentGroupBy, true);
      }
    },
    [
      groupBy,
      bin,
      dispatch,
      updateBin,
      updateSessionStorage,
      updateField,
      onBinsChanged,
      checkedLegendItemsRef
    ]
  );

  const resetGroupByBin = useCallback(() => {
    setPreRaise(null);
    pendingRaiseRef.current = null;
    if (deps.isWell) useFilterLandingBinStore.getState().setPending(null);
    const newLocals: Record<string, mBinSize> = {};
    Object.keys(localBinsRef.current).forEach((key) => {
      if (key !== groupBy.property) newLocals[key] = { ...localBinsRef.current[key] };
    });
    localBinsRef.current = newLocals;
    setStoredBinsToSession(newLocals);

    onBinsChanged();

    updateGroupBy(
      groupBy.property,
      deps.columnsDictionary,
      updateField,
      null,
      groupBy.forecastFolder,
      groupBy.fullContextTitleWithNormalization,
      groupBy.partialContextTitle,
      groupBy.isForecastToggleOn,
      groupBy.pdenSource,
      deps.groupBy.entityKind,
      defaults.binSettings,
      groupBy.dataType,
      groupBy.performanceDateRange,
      maxBinDefaultsEnabled
    );
  }, [
    groupBy,
    defaults,
    deps.groupBy.entityKind,
    deps.columnsDictionary,
    updateField,
    localBinsRef,
    onBinsChanged,
    maxBinDefaultsEnabled
  ]);

  const toggleLockBins = useCallback(
    (isLocked?: boolean) => {
      const nextIsLocked = isLocked !== undefined ? isLocked : !bin.IsLocked;
      if (bin.IsLocked) {
        onBinsChanged();
      }
      // A bin waiting for the well filter takes the lock where it waits: dispatched now,
      // the widgets would group the filter before it by it.
      const landing = useFilterLandingBinStore.getState();
      if (deps.isWell && landing.pending?.property === groupBy.property) {
        landing.amendPending({ ...landing.pending.bin, IsLocked: nextIsLocked });
        updateBin({ IsLocked: nextIsLocked });
        return;
      }
      const newBin = Object.assign({}, bin?.BinSize !== "" ? bin : groupBy.bin, {
        IsLocked: nextIsLocked
      });
      const newGroupBy = Object.assign({}, groupBy, { bin: newBin });
      dispatch(setGlobalGroupBy(newGroupBy));
      updateBin({ IsLocked: newBin.IsLocked });
    },
    [dispatch, groupBy, bin, deps.isWell, updateBin, onBinsChanged]
  );

  // Locks the bins and grows MaxBins (never shrinks it) so every checked value keeps its
  // own bin instead of folding back into Others.
  const lockBinsToFit = useCallback(
    (checkedCount: number) => {
      // A bin still waiting for its filter (a restore, a saved filter's) is the one these
      // checks are grouped by, so they are fitted to it and it lands fitted with them.
      const landing = useFilterLandingBinStore.getState();
      const queuedBin =
        deps.isWell && landing.pending?.property === groupBy.property
          ? landing.pending.bin
          : null;
      const maxBins = (queuedBin ?? groupBy.bin)?.MaxBins;
      const fits = !exceedsMaxBins(maxBins, checkedCount);
      if (!fits && preRaiseRef.current?.property !== groupBy.property) {
        setPreRaise({ property: groupBy.property, maxBins });
      }
      // The queued bin is locked (and fitted) where it waits: dispatched now, the widgets
      // would group the filter before it by it.
      if (queuedBin) {
        const locked: mBinSize = {
          ...queuedBin,
          ...(fits ? {} : { MaxBins: checkedCount }),
          IsLocked: true
        };
        landing.setPending({ property: groupBy.property, bin: locked });
        storeBin(locked);
        return;
      }
      if (!fits) {
        const pending = pendingRaiseRef.current;
        pendingRaiseRef.current = {
          property: groupBy.property,
          maxBins: Math.max(
            pending?.property === groupBy.property ? pending.maxBins : 0,
            checkedCount
          )
        };
      }
      if (groupBy.bin?.IsLocked) return;
      const newBin: mBinSize = {
        ...(bin?.BinSize !== "" ? bin : groupBy.bin),
        IsLocked: true
      };
      dispatch(setGlobalGroupBy({ ...groupBy, bin: newBin }));
      storeBin(newBin);
    },
    [dispatch, groupBy, bin, deps.isWell, storeBin, setPreRaise]
  );

  // The raise lands with the filter that checks its values. Sent ahead of it, every
  // widget refetched the old filter with a bin to spare and filled it with an
  // unchecked value; grouped first with the old MaxBins, the checked values all
  // keep their own bins anyway. An earlier request landing first carries fewer
  // checks, so the raise goes only as far as the landed filter's checks and the
  // rest waits for a later one.
  useEffect(() => {
    const pending = pendingRaiseRef.current;
    if (!pending) return;
    if (pending.property !== groupBy.property) {
      pendingRaiseRef.current = null;
      return;
    }
    const landed = checkedValueCountInQuery(currentFilter?.query, pending.property);
    if (landed >= pending.maxBins) pendingRaiseRef.current = null;
    const maxBins = Math.min(landed, pending.maxBins);
    if (!exceedsMaxBins(groupBy.bin?.MaxBins, maxBins)) return;
    const raised = { MaxBins: maxBins, IsLocked: true };
    dispatch(setGlobalGroupBy({ ...groupBy, bin: { ...groupBy.bin, ...raised } }));
    storeBin({ ...bin, ...raised });
    // A filter can land under the id it already had; lastUpdated marks every landing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterId.id, filterId.lastUpdated]);

  // Once nothing is checked, MaxBins goes back to its value before the raise.
  // A well filter lands after a restore or the last uncheck, so their bins ride with its
  // id (see filterLandingBinStore); dispatched now, widgets would group the old filter by them.
  const setBinWithFilter = useCallback(
    (next: mBinSize) => {
      if (deps.isWell) {
        useFilterLandingBinStore
          .getState()
          .setPending({ property: groupBy.property, bin: next });
      } else {
        dispatch(setGlobalGroupBy({ ...groupBy, bin: next }));
      }
    },
    [dispatch, groupBy, deps.isWell]
  );

  const restoreMaxBins = useCallback(() => {
    const raise = preRaiseRef.current;
    setPreRaise(null);
    pendingRaiseRef.current = null;
    if (raise?.property !== groupBy.property) return;
    const landing = useFilterLandingBinStore.getState();
    const queued = deps.isWell && landing.pending?.property === groupBy.property;
    if (queued) {
      // A bin still waiting (a saved filter's raise) is restored where it waits, keeping
      // its other settings; the filter clearing the checks lands it.
      landing.setPending({
        property: groupBy.property,
        bin: { ...landing.pending.bin, MaxBins: raise.maxBins }
      });
    } else if (checkedValueCountInQuery(currentFilter?.query, groupBy.property) === 0) {
      // The filter without the checks has landed already; nothing is left to ride.
      dispatch(
        setGlobalGroupBy({ ...groupBy, bin: { ...groupBy.bin, MaxBins: raise.maxBins } })
      );
    } else {
      setBinWithFilter({ ...groupBy.bin, MaxBins: raise.maxBins });
    }
    storeBin({ ...bin, MaxBins: raise.maxBins });
  }, [
    dispatch,
    groupBy,
    bin,
    currentFilter,
    deps.isWell,
    storeBin,
    setPreRaise,
    setBinWithFilter
  ]);

  // Checked-Item Filtering bumps autoLockRequestId (instead of calling this
  // module through a ref-trampoline) whenever checking items should auto-lock
  // the bins or outgrow MaxBins; locking and fitting stay owned here.
  const autoLockRequestIdRef = useRef(autoLockRequestId);
  useEffect(() => {
    if (autoLockRequestId === autoLockRequestIdRef.current) return;
    autoLockRequestIdRef.current = autoLockRequestId;
    lockBinsToFit(autoLockCheckedCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLockRequestId]);

  const togglePlaceholderBinsEnabled = useCallback(async () => {
    dispatch(setPlaceholderBinsEnabled(!placeholderBinsEnabled));
  }, [dispatch, placeholderBinsEnabled]);

  // Verbatim from GroupByFilterActivity.tsx's updateBinSettingsFromSavedFilter:
  // restoring a saved filter dispatches setGlobalGroupBy + a RAW setBin (no
  // session-merge) only for the property that matches the current groupBy.
  // Exposed so useSavedFiltersController can restore bins without owning `bin`.
  const applyRestoredBin = useCallback(
    (property: string, binSettings: mBinSize, checkedCount: number) => {
      if (property !== groupBy.property) return;
      pendingRaiseRef.current = null;
      // The fresh-session default only targets the Well focus.
      if (deps.isWell) retireFreshSessionMaxBinDefault();
      // A filter saved with more checked values than its MaxBins gets the same fit
      // as checking them would; storage keeps the saved MaxBins.
      const raises = exceedsMaxBins(binSettings.MaxBins, checkedCount);
      setPreRaise(raises ? { property, maxBins: binSettings.MaxBins } : null);
      const restored = raises
        ? { ...binSettings, MaxBins: checkedCount, IsLocked: true }
        : binSettings;
      setBinWithFilter(restored);
      setBin(restored);
    },
    [groupBy.property, deps.isWell, setPreRaise, setBinWithFilter]
  );

  return {
    bin,
    canEditBin,
    minCountWarningVisible,
    updateBin,
    updateField,
    updateGroupByBin,
    toggleLockBins,
    lockBinsToFit,
    restoreMaxBins,
    isMaxBinsRaised: raisedProperty === groupBy.property,
    resetGroupByBin,
    togglePlaceholderBinsEnabled,
    placeholderBinsEnabled,
    applyRestoredBin
  };
};

export { useBinSettingsController as default };
